import type { Partner } from "@expert/shared-types";
import type { AbstractAnalyticsDispatcher } from "@soluto-private/eventualize";

import { useQuery } from "@tanstack/react-query";

import type { CustomerDetailsPayload } from "@/customer-info";

import { getCustomerDetails } from "@/customer-info/api";
import { CUSTOMER_DETAILS_QUERY_KEY } from "@/query/queryServices";
import { useLogger } from "@/sdk";
import { DEFAULT_COUNTRY_CODE, validatePhoneNumber } from "@/shared-utils";

interface UseGetCustomerDetailsParams {
    mdn?: string;
    sessionId?: string;
    partner: Partner;
    dispatcher?: AbstractAnalyticsDispatcher;
    customerDetailsCallback?: (result: CustomerDetailsPayload) => void;
    verificationType: string;
    shouldSkipMdnValidation?: boolean;
}

export function useGetCustomerDetails({
    mdn,
    partner,
    dispatcher,
    sessionId,
    customerDetailsCallback,
    verificationType,
    shouldSkipMdnValidation = false,
}: UseGetCustomerDetailsParams) {
    const logger = useLogger({ module: "useGetCustomerDetails" });

    const { data, isLoading } = useQuery({
        queryKey: [CUSTOMER_DETAILS_QUERY_KEY, mdn],
        queryFn: async () => {
            return getCustomerDetails(logger, {
                mdn: mdn ?? "",
                partner,
                sessionId,
                searchType: "phoneNumber",
                dispatcher,
                customerDetailsCallback,
                modelResultsMdn: mdn,
            });
        },
        enabled:
            verificationType === "phoneNumber" &&
            validatePhoneNumber(mdn, DEFAULT_COUNTRY_CODE, shouldSkipMdnValidation).valid,
        // Avoid refetching just because the component remounted (e.g. entering "Edit" mode).
        // We only want to fetch when the query key changes (MDN changes) or via an explicit refetch.
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
    };
}
